import { useState, useCallback, useEffect, useRef } from "react";
import { Flex, Button, Text, TextArea, Em } from "@radix-ui/themes";
import { PlusIcon, ArrowRightIcon } from "@radix-ui/react-icons";
import styles from "../AutomationConfiguration.module.scss";
import cx from "classnames";

interface OptionalTextareaProps {
  value: string | undefined;
  onChange: (value: string) => void;
  buttonText?: string;
  label?: string;
  description?: string;
  className?: string;
  optional?: boolean;
}

export function OptionalTextarea({
  value,
  onChange,
  buttonText = "Add",
  description,
  className,
  optional = true,
}: OptionalTextareaProps) {
  const [innerValue, setInnerValue] = useState(value ?? "");
  const [open, setOpen] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const isEmpty = value == null || value === "";

  const handleChange = useCallback((newValue: string) => {
    setInnerValue(newValue);
  }, []);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setInnerValue(value ?? "");
  }, [value]);

  const handleDelete = useCallback(() => {
    setOpen(false);
    onChange("");
  }, [onChange]);

  const hadleSave = useCallback(() => {
    setOpen(false);
    onChange(innerValue);
  }, [onChange, innerValue]);

  useEffect(() => {
    setInnerValue(value ?? "");
  }, [value]);

  const setTextareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;

    if (el != null) {
      el.focus();
      el.style.height = "auto";
      // Set the height to match the scrollHeight
      el.style.height = el.scrollHeight + "px";

      // Scroll to the bottom of the textarea
      setTimeout(() => {
        el.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      }, 0);
    }
  }, []);

  useEffect(() => {
    // Reset the height to get the new scrollHeight
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      // Set the height to match the scrollHeight
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [innerValue]);

  if (isEmpty && !open) {
    return (
      <Button
        color="gray"
        variant="soft"
        size="1"
        onClick={() => setOpen(true)}
      >
        <PlusIcon height={"15px"} width={"15px"} />
        <Text size={"2"} weight={"regular"} highContrast>
          {buttonText}
        </Text>

        {optional && (
          <Text size={"2"} weight={"regular"}>
            <Em>(optional)</Em>
          </Text>
        )}
      </Button>
    );
  }

  if (!isEmpty && !open) {
    return (
      <Button
        color="gray"
        variant="soft"
        size="1"
        onClick={() => setOpen(true)}
        className={cx(styles.OptionalTextareaPreview, className)}
      >
        <Flex
          gap={"2"}
          align={"center"}
          className={styles.OptionalTextareaPreviewInner}
        >
          <ArrowRightIcon />

          <Text size={"2"} highContrast truncate>
            {value}
          </Text>
        </Flex>
      </Button>
    );
  }

  return (
    <Flex
      width={"100%"}
      p={"3"}
      direction={"column"}
      gap={"2"}
      className={cx(styles.OptionalTextarea, className)}
    >
      <TextArea
        ref={setTextareaRef}
        variant={"soft"}
        color={"gray"}
        value={innerValue ?? ""}
        onChange={(e) => handleChange(e.target.value)}
        className={styles.TextArea}
        placeholder={"Add"}
      />

      {description != null && (
        <Text size={"2"} color={"gray"}>
          <Em>{description}</Em>
        </Text>
      )}

      <Flex justify={"between"}>
        <Button
          color="gray"
          variant="outline"
          highContrast
          size="1"
          onClick={handleDelete}
        >
          Delete
        </Button>

        <Flex gap={"2"}>
          <Button
            color="gray"
            variant="outline"
            highContrast
            size="1"
            onClick={handleCancel}
          >
            Cancel
          </Button>

          <Button
            color="gray"
            variant="solid"
            highContrast
            size="1"
            onClick={hadleSave}
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
